import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import API_BASE_URL from "../../../config/api";
import { FaTasks, FaChartLine, FaCheckCircle, FaUsers, FaBook, FaExternalLinkAlt, FaUserTie } from "react-icons/fa";

function ExaminerHomeContainer() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    // eslint-disable-next-line
    const [announcements, setAnnouncements] = useState([]);
    const [userName, setUserName] = useState("Examiner");
    // eslint-disable-next-line
    const [department, setDepartment] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const user = JSON.parse(localStorage.getItem("user")) || {};
                setUserName(user.name || "Examiner");
                setDepartment(user.department || "Department");

                const response = await fetch(
                    `${API_BASE_URL}/api/projects/guide/${user.email}`
                );
                const result = await response.json();

                if (response.ok && result.success) {
                    setProjects(result.data || []);
                }

                const announcementsResponse = await fetch(
                    `${API_BASE_URL}/api/projects/announcements/general`
                );
                const announcementsResult = await announcementsResponse.json();
                if (announcementsResponse.ok) {
                    setAnnouncements(
                        announcementsResult.data || announcementsResult || []
                    );
                }
            } catch (error) {
                console.error("Error loading data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        AOS.init({ duration: 1000 });
    }, []);

    const gotoEvaluation = () => {
        navigate("/examiner/eval");
    };

    const viewProject = (projectId) => {
        navigate(`/project/${projectId}`);
    };

    const truncateText = (text, maxLength) => {
        if (!text) return "";
        return text.length > maxLength
            ? text.substring(0, maxLength) + "..."
            : text;
    };

    const getGuideEmail = (guidePref) => {
        if (!guidePref) return "";
        if (typeof guidePref === "string") return guidePref;
        if (guidePref.email) return guidePref.email;
        return "";
    };

    const renderCoGuides = (coGuides) => {
        if (!coGuides) return null;

        return Object.entries(coGuides)
            .filter(([_, value]) => {
                if (typeof value === "string") return value.trim() !== "";
                if (value && typeof value === "object")
                    return value.email && value.email.trim() !== "";
                return false;
            })
            .map(([key, value], idx) => (
                <li key={key} className="text-gray-600 text-sm">
                    {idx + 1}. {typeof value === "string" ? value : value.email}
                </li>
            ));
    };

    return (
        <div className="min-h-screen p-4 md:p-8 font-sans">
            {/* Header Section */}
            <div
                className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8"
                data-aos="fade-down"
            >
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <FaUserTie className="text-[#B7202E]" /> Welcome, {userName}
                    </h1>
                    <p className="text-gray-500 mt-2">Manage and evaluate your assigned projects.</p>
                </div>
                <button
                    className="bg-[#B7202E] text-white px-6 py-3 rounded-xl font-bold shadow-sm hover:bg-[#961925] hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-2"
                    onClick={gotoEvaluation}
                >
                    <FaChartLine /> Track Progress
                </button>
            </div>

            {/* Projects Grid */}
            <div>
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <FaTasks className="text-[#B7202E]" /> Your Assigned Projects
                </h2>

                {loading ? (
                    <div className="flex flex-col items-center justify-center min-h-[40vh]">
                        <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
                        <p className="text-gray-500 font-medium">Loading projects...</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {projects && projects.length > 0 ? (
                            projects.map((project, index) => (
                                <div
                                    key={project._id || index}
                                    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col h-full group"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="h-1.5 w-full bg-[#B7202E]"></div>

                                    <div className="p-6 flex-1 flex flex-col gap-4">
                                        <div className="flex justify-between items-start gap-4">
                                            <h3 className="text-lg font-bold text-gray-800 leading-tight group-hover:text-[#B7202E] transition-colors line-clamp-2">
                                                {truncateText(project.projectTitle, 60)}
                                            </h3>
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border whitespace-nowrap ${(project.status === 'approved' || project.status === 'guide-assigned') ? 'bg-green-50 text-green-700 border-green-200' :
                                                project.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                                    'bg-gray-50 text-gray-600 border-gray-200'
                                                }`}>
                                                {project.status ? project.status.replace("-", " ") : "Pending"}
                                            </span>
                                        </div>

                                        <div className="space-y-3 pt-2">
                                            <div className="flex items-start gap-2 text-sm text-gray-600">
                                                <FaUsers className="mt-1 text-gray-400" />
                                                <div className="flex-1">
                                                    <span className="font-semibold text-gray-700 block text-xs uppercase tracking-wider mb-1">Team:</span>
                                                    <div className="flex flex-wrap gap-1">
                                                        {project.teamMembers?.map((member, idx) => (
                                                            <span key={idx} className="bg-gray-100 px-2 py-0.5 rounded text-xs text-gray-600">
                                                                {member}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-2 text-sm text-gray-600">
                                                <FaBook className="mt-1 text-gray-400" />
                                                <div>
                                                    <span className="font-semibold text-gray-700 block text-xs uppercase tracking-wider mb-1">Domain:</span>
                                                    <span>{project.domain?.preference1?.name || "Not specified"}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-2 text-sm text-gray-600">
                                                <FaCheckCircle className="mt-1 text-gray-400" />
                                                <div>
                                                    <span className="font-semibold text-gray-700 block text-xs uppercase tracking-wider mb-1">Guide:</span>
                                                    <span className="text-xs">
                                                        {project.guide?.preference1 ? getGuideEmail(project.guide.preference1) : "Not assigned"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-auto pt-4 border-t border-gray-50">
                                            <button
                                                onClick={() => viewProject(project._id || index)}
                                                className="w-full bg-[#506eec] hover:bg-[#3a5bbf] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                                            >
                                                View Details <FaExternalLinkAlt className="text-xs" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
                                <FaTasks className="mx-auto text-4xl text-gray-300 mb-4" />
                                <h3 className="text-lg font-bold text-gray-800 mb-1">No Projects Assigned</h3>
                                <p className="text-gray-500">You haven't been assigned any projects to examine yet.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ExaminerHomeContainer;
